import React, { useMemo, useState } from "react";
import { Grid2 as Grid, Box, Checkbox, FormControlLabel, Typography, Stack, TextField, InputAdornment, Chip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification, useTranslate } from "@refinedev/core";

import { Modal, ModalRef, PlansAutocomplete } from "@shared/components";
import { AgreementErpPlan } from "@modules/integration/services/queries/getAgreementErpPlans/types";
import { MapIntegrationPlan } from "@modules/mapIntegrationPlans/types";
import { createMapIntegrationPlanService } from "@modules/mapIntegrationPlans/services/mutations/createMapIntegrationPlan/service";

interface BulkMapIntegrationPlanModalProps {
  modalRef: React.RefObject<ModalRef>;
  /** ID do convênio para filtrar planos no autocomplete */
  agreement?: number | string | null;
  /** Planos retornados do ERP para este convênio */
  erpPlans: AgreementErpPlan[];
  /** Mapeamentos já existentes, para evitar duplicidades */
  mappings: MapIntegrationPlan[];
}

interface BulkMapIntegrationPlanFormValues {
  plans: number[];
  erpSlugs: string[];
}

export const BulkMapIntegrationPlanModal = React.memo(
  ({ modalRef, agreement, erpPlans, mappings }: BulkMapIntegrationPlanModalProps) => {
    const translate = useTranslate();
    const queryClient = useQueryClient();
    const { open } = useNotification();
    const [searchErp, setSearchErp] = useState("");
    const [filterUnmapped, setFilterUnmapped] = useState(false);

    const methods = useForm<BulkMapIntegrationPlanFormValues>({
      defaultValues: {
        plans: [],
        erpSlugs: [],
      },
      mode: "onBlur",
    });

    const existingCombinations = useMemo(() => {
      const set = new Set<string>();
      mappings.forEach((m) => {
        set.add(`${m.plan}::${m.plan_slug_integration}`);
      });
      return set;
    }, [mappings]);

    // Criar mapa de slugs ERP para verificar quais estão mapeados
    const mappedErpSlugs = useMemo(() => {
      const set = new Set<string>();
      mappings.forEach((m) => {
        set.add(m.plan_slug_integration);
      });
      return set;
    }, [mappings]);

    // Filtrar planos ERP baseado na busca e filtro de não mapeados
    const filteredErpPlans = useMemo(() => {
      let filtered = erpPlans;

      // Filtro de busca
      if (searchErp.trim()) {
        const term = searchErp.trim().toLowerCase();
        filtered = filtered.filter(
          (erp) =>
            (erp.name_plan ?? "").toLowerCase().includes(term) ||
            (erp.slug ?? "").toLowerCase().includes(term) ||
            (erp.type_mapping ?? "").toLowerCase().includes(term)
        );
      }

      // Filtro de não mapeados
      if (filterUnmapped) {
        filtered = filtered.filter((erp) => !mappedErpSlugs.has(erp.slug));
      }

      return filtered;
    }, [erpPlans, searchErp, filterUnmapped, mappedErpSlugs]);

    const { mutate, isPending } = useMutation({
      mutationFn: async ({ plans, erpSlugs }: BulkMapIntegrationPlanFormValues) => {
        if (!plans?.length || !erpSlugs?.length) {
          throw new Error(translate("subscription_mapintegrationplan_admin.validation.bulk_required"));
        }

        const creations: Promise<unknown>[] = [];

        plans.forEach((planId) => {
          erpSlugs.forEach((slug) => {
            const key = `${planId}::${slug}`;
            if (existingCombinations.has(key)) {
              return;
            }

            creations.push(
              createMapIntegrationPlanService({
                plan: planId,
                plan_slug_integration: slug,
                plan_slug_integration_list: undefined,
                price_integration: undefined,
              } as any)
            );
          });
        });

        if (!creations.length) {
          throw new Error(translate("subscription_mapintegrationplan_admin.validation.bulk_no_new"));
        }

        await Promise.all(creations);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["subscription_mapintegrationplan_admin"],
          refetchType: "all",
        });

        open?.({
          type: "success",
          message: translate("subscription_mapintegrationplan_admin.success.bulk_create"),
        });

        modalRef.current?.close();
      },
      onError: (error: any) => {
        const message =
          error?.message ||
          translate("subscription_mapintegrationplan_admin.errors.bulk_generic");

        open?.({
          type: "error",
          message,
        });
      },
    });

    const handleToggleErpSlug = (slug: string, checked: boolean) => {
      const current = methods.getValues("erpSlugs") ?? [];
      const next = checked ? [...current, slug] : current.filter((s) => s !== slug);
      methods.setValue("erpSlugs", next, { shouldDirty: true });
    };

    const handleSelectAll = (checked: boolean) => {
      if (checked) {
        const allSlugs = filteredErpPlans.map((p) => p.slug).filter(Boolean) as string[];
        methods.setValue("erpSlugs", allSlugs, { shouldDirty: true });
      } else {
        // Remover apenas os slugs dos planos filtrados
        const filteredSlugs = filteredErpPlans.map((p) => p.slug).filter(Boolean) as string[];
        const current = methods.getValues("erpSlugs") ?? [];
        const next = current.filter((slug) => !filteredSlugs.includes(slug));
        methods.setValue("erpSlugs", next, { shouldDirty: true });
      }
    };

    const selectedErpSlugs = methods.watch("erpSlugs") ?? [];

    return (
      <Modal
        ref={modalRef}
        title={translate("subscription_mapintegrationplan_admin.titles.bulk_create")}
        methods={methods}
        onConfirm={mutate as any}
        isPending={isPending}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <PlansAutocomplete
              multiple
              name="plans"
              label={translate("subscription_mapintegrationplan_admin.fields.plan_bulk")}
              agreement={agreement}
              required
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Stack spacing={1.5}>
              {/* Filtros */}
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  placeholder="Buscar planos ERP..."
                  value={searchErp}
                  onChange={(e) => setSearchErp(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filterUnmapped}
                      onChange={(_, checked) => setFilterUnmapped(checked)}
                    />
                  }
                  label="Apenas não mapeados"
                />
              </Stack>

              {/* Lista de planos ERP */}
              <Box
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 320,
                  overflow: "auto",
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">
                    Planos ERP ({filteredErpPlans.length} de {erpPlans.length})
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          filteredErpPlans.length > 0 &&
                          filteredErpPlans.every((erp) => selectedErpSlugs.includes(erp.slug))
                        }
                        indeterminate={
                          filteredErpPlans.some((erp) => selectedErpSlugs.includes(erp.slug)) &&
                          !filteredErpPlans.every((erp) => selectedErpSlugs.includes(erp.slug))
                        }
                        onChange={(_, checked) => handleSelectAll(checked)}
                      />
                    }
                    label="Selecionar todos"
                  />
                </Stack>

                {filteredErpPlans.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {erpPlans.length === 0
                      ? translate("subscription_mapintegrationplan_admin.messages.no_erp_plans")
                      : "Nenhum plano ERP encontrado com os filtros aplicados."}
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {filteredErpPlans.map((erp) => {
                      const isMapped = mappedErpSlugs.has(erp.slug);
                      return (
                        <FormControlLabel
                          key={erp.id}
                          control={
                            <Checkbox
                              checked={selectedErpSlugs.includes(erp.slug)}
                              onChange={(_, checked) => handleToggleErpSlug(erp.slug, checked)}
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Box>
                                <Typography variant="body2">{erp.name_plan}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {erp.slug} • {erp.type_mapping ?? "—"}
                                </Typography>
                              </Box>
                              {isMapped && (
                                <Chip
                                  label="Mapeado"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{ height: "20px", fontSize: "0.65rem" }}
                                />
                              )}
                            </Box>
                          }
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Modal>
    );
  }
);

